import { Component, ElementRef, HostListener, Input, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LanguageService, LangItem } from '../../services/language.service';

@Component({
  selector: 'app-language-selector',
  standalone: true,
  imports: [CommonModule],
  template: `
    <nav class="lang" [class.inline]="inline" [class.dropup]="dropUp" aria-label="Selecionar idioma">
      <!-- Botão principal (pill) -->
      <button type="button" class="lang__btn" aria-haspopup="menu" [attr.aria-expanded]="open" (click)="toggle()">
        <img class="flag" [src]="flagUrl(current)" alt="" width="20" height="20" />
        <!-- Fechado: sigla / Aberto: nome completo -->
        <span class="code" *ngIf="!open">{{ current?.toUpperCase() }}</span>
        <span class="name" *ngIf="open">{{ currentLabel }}</span>
        <svg class="chev" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M7 10l5 5 5-5" />
        </svg>
      </button>

      <!-- Dropdown -->
      <div *ngIf="open" class="lang__menu" role="menu" aria-label="Idiomas">
        <button
          type="button"
          class="lang__option"
          *ngFor="let l of langs; trackBy: track"
          role="menuitemradio"
          [attr.aria-checked]="current === l.code"
          (click)="set(l.code)"
        >
          <img class="flag" [src]="flagUrl(l.code)" alt="" width="22" height="22" />
          <span class="label">{{ labelFor(l.code, l.label) }}</span>
        </button>
      </div>
    </nav>
  `,
  styles: [
    `
      /* ... tudo o que já tens ... */

      .lang__menu {
        position: absolute;
        right: 0;
        top: calc(100% + 8px);
        min-width: 200px;
        padding: 8px;
        background: #fff;
        border: 1px solid #e7e7f2;
        border-radius: 14px;
        box-shadow:
          0 12px 30px rgba(16, 24, 40, 0.18),
          0 2px 6px rgba(16, 24, 40, 0.08);
        z-index: 5;
        animation: dropdown 0.12s ease-out;
      }

      /* Quando o nav tiver a classe .dropup, o menu abre para cima */
      .lang.dropup .lang__menu {
        top: auto;
        bottom: calc(100% + 8px);
      }

      /* Fica onde o colocares: ocupa a linha e encosta conteúdo à direita */
      :host {
        display: block;
        width: 100%;
      }
      .lang {
        position: relative;
        display: flex;
        justify-content: flex-end;
      }
      /* Se precisares inline num toolbar: [inline]="true" */
      .lang.inline {
        display: inline-flex;
        justify-content: flex-start;
        width: auto;
      }

      /* Botão (pill) */
      .lang__btn {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 6px 12px;
        border-radius: 9999px;
        background: #eff1f7;
        border: 1px solid rgba(16, 24, 40, 0.08);
        box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06);
        font-size: 14px;
        font-weight: 600;
        line-height: 1;
        color: #1c1463;
        cursor: pointer;
        transition:
          box-shadow 0.15s ease,
          background 0.15s ease;
      }
      .lang__btn:hover {
        background: #e8ebf5;
        box-shadow: 0 6px 18px rgba(16, 24, 40, 0.12);
      }
      .lang__btn:focus-visible {
        outline: 3px solid #a4b8ff;
        outline-offset: 2px;
      }

      .flag {
        width: 20px;
        height: 20px;
        border-radius: 50%;
        display: block;
        box-shadow: 0 0 0 2px #fff;
        object-fit: cover;
      }
      .code {
        text-transform: uppercase;
        letter-spacing: 0.2px;
      }
      .name {
        white-space: nowrap;
      }

      .chev {
        width: 16px;
        height: 16px;
        fill: none;
        stroke: currentColor;
        stroke-width: 2;
        transition: transform 0.15s ease;
      }
      .lang__btn[aria-expanded='true'] .chev {
        transform: rotate(180deg);
      }

      /* Dropdown */
      .lang__menu {
        position: absolute;
        right: 0;
        top: calc(100% + 8px);
        min-width: 200px;
        padding: 8px;
        background: #fff;
        border: 1px solid #e7e7f2;
        border-radius: 14px;
        box-shadow:
          0 12px 30px rgba(16, 24, 40, 0.18),
          0 2px 6px rgba(16, 24, 40, 0.08);
        z-index: 5;
        animation: dropdown 0.12s ease-out;
      }
      @keyframes dropdown {
        from {
          opacity: 0;
          transform: translateY(-4px) scale(0.98);
        }
        to {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
      }

      .lang__option {
        width: 100%;
        display: flex;
        align-items: center;
        gap: 10px;
        background: transparent;
        border: 0;
        cursor: pointer;
        padding: 12px 16px; /* Tap target melhorado para 48px */
        border-radius: 10px;
        text-align: left;
        font-size: 14px;
        color: #1c1463;
        font-weight: 500;
        min-height: 48px;
      }
      .lang__option .flag {
        width: 22px;
        height: 22px;
      }
      .lang__option:hover {
        background: #eef2ff;
      }
      .lang__option[aria-checked='true'] {
        background: #eef2ff;
        font-weight: 600;
      }
    `,
  ],
})
export class LanguageSelectorComponent implements OnDestroy {
  /** por defeito: ocupa a linha e encosta à direita; se precisares inline, usa [inline]="true" */
  @Input() inline = false;
  @Input() dropUp = false;
  @Input() flagsPath = 'assets/flags';

  langs: LangItem[] = this.langSvc.list();
  current = this.langSvc.current;
  open = false;

  private destroy$ = new Subject<void>();

  /** mapear códigos -> ficheiros de bandeira (ex.: EN -> GB) */
  private flagAlias: Record<string, string> = {
    en: 'gb',
    en_GB: 'gb',
    en_US: 'us',
    pt: 'pt',
    pt_PT: 'pt',
    es: 'es',
    it: 'it',
    fr: 'fr',
  };

  /** nomes legíveis (fallback se o serviço só tiver siglas) */
  private labelsMap: Record<string, string> = {
    en: 'English',
    es: 'Español',
    it: 'Italiano',
    pt: 'Português',
    fr: 'Français',
  };

  constructor(
    private langSvc: LanguageService,
    private el: ElementRef<HTMLElement>,
  ) {
    this.langSvc.current$.pipe(takeUntil(this.destroy$)).subscribe((c: string) => (this.current = c));
  }

  /** nome completo do idioma atual para o botão aberto */
  get currentLabel(): string {
    return this.labelFor(this.current);
  }

  /** devolve nome completo usando: LangItem.label -> labelsMap -> SIGLA */
  labelFor(code?: string, fallback?: string): string {
    if (!code) return '';
    const fromService = this.langs.find(x => x.code === code)?.label;
    return fromService && fromService.length > 2
      ? fromService
      : (this.labelsMap[code] ?? fallback ?? code.toUpperCase());
  }

  toggle() {
    this.open = !this.open;
  }

  set(code: string) {
    this.langSvc.use(code);
    this.open = false;
  }

  track = (_: number, l: LangItem) => l.code;

  flagUrl(code?: string) {
    if (!code) return '';
    const key = this.flagAlias[code] || code;
    return `${this.flagsPath}/${key}.svg`;
  }

  @HostListener('document:click', ['$event'])
  onDocClick(ev: Event) {
    if (!this.el.nativeElement.contains(ev.target as Node)) this.open = false;
  }

  @HostListener('document:keydown.escape')
  onEsc() {
    this.open = false;
  }

  ngOnDestroy(): void {
    if (!this.destroy$.closed) {
      this.destroy$.next();

      this.destroy$.complete();
    }
  }
}
